/**
 * FiveBars
 *
 * Custom loader animation that replicates Mantine v6 "bars" style.
 *
 * Why we need it:
 * – Mantine v8 changed the default bars loader animation/shape.
 * – To keep Blockdaemon UI visually consistent, we re-implement the old v6 look.
 *
 * Notes:
 * – This is a self-contained replacement, no wrapper is required.
 * – It integrates directly with Mantine’s <Loader type="bars" />.
 * – Consumers can continue importing Loader from '@mantine/core'; no need to import from design-system.
 * – Other loader types (oval, dots) are unaffected.
 */
import React from 'react';

// self-contained custom 5-bars loader component to keep UI comopatibility
export const FiveBars = React.forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
  function FiveBars(props, ref) {
    const wrap: React.CSSProperties = {
      fontSize: 'var(--loader-size)',
      lineHeight: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1em',
      height: '1em',
      color: 'var(--loader-color, currentColor)',
    };

    const bar: React.CSSProperties = {
      display: 'inline-block',
      width: '0.08em',
      height: '0.72em',
      marginInline: '0.030em',
      background: 'currentColor',
      borderRadius: '0.08em',
      transformOrigin: 'center',
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      transform: 'translateZ(0)',
      animation: 'bd-bars-center 1100ms cubic-bezier(0.22, 0.9, 0.2, 1) infinite',
    };

    type WithPeakVar = React.CSSProperties & Record<'--bd-peak', number>;
    const peak = (v: number): WithPeakVar => ({ '--bd-peak': v }) as WithPeakVar;

    return (
      <span ref={ref} {...props} style={{ ...wrap, ...props.style }}>
        <i style={{ ...bar, animationDelay: '-540ms', ...peak(0.88) }} />
        <i style={{ ...bar, animationDelay: '-180ms', ...peak(0.96) }} />
        <i style={{ ...bar, animationDelay: '0ms', ...peak(1.0) }} />
        <i style={{ ...bar, animationDelay: '-180ms', ...peak(0.96) }} />
        <i style={{ ...bar, animationDelay: '-540ms', ...peak(0.88) }} />

        <style>{`
          @keyframes bd-bars-center {
            0%   { transform: scaleY(0.38); }
            14%  { transform: scaleY(var(--bd-peak, 1)); }
            26%  { transform: scaleY(0.88); }
            42%  { transform: scaleY(0.72); }
            62%  { transform: scaleY(0.58); }
            82%  { transform: scaleY(0.44); }
            100% { transform: scaleY(0.38); }
          }
        `}</style>
      </span>
    );
  }
);
